import React from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment-timezone'
import { Badge, ContentPlaceholder, Row, Text, FloatingMenu, MenuItem, MenuDivider, Icon } from '@evertel/web/ui'
import { APIDataDepartmentAgreement } from '@evertel/types'

interface AgreementCardProps {
    agreement: APIDataDepartmentAgreement
    canDelete: boolean
    onEdit: (agreement: APIDataDepartmentAgreement) => void
    onPreview: (agreement: APIDataDepartmentAgreement) => void
    onVersionHistory: (agreement: APIDataDepartmentAgreement) => void
    onDelete: (agreement: APIDataDepartmentAgreement) => void
}

const AgreementCard: React.FC<AgreementCardProps> = observer(({
    agreement,
    canDelete,
    onEdit,
    onPreview,
    onVersionHistory,
    onDelete
}) => {
    const name = agreement.currentDocument?.name ?? agreement.title
    const version = agreement.currentDocument?.version
    const updatedAt = agreement.updatedDate
        ? moment(agreement.updatedDate).format('MMM D, YYYY')
        : null

    return (
        <div className="border rounded p-3 mb-3">
            <Row valign="start" align="between" className="mb-2">
                <div style={{ flex: 1, minWidth: 0 }} className="me-2">
                    <span style={{ fontWeight: 'bold' }}>{name}</span>
                    {version && (
                        <Badge
                            color="primary"
                            variant="soft"
                            className="ms-2"
                            style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}
                        >
                            v{version}
                        </Badge>
                    )}
                </div>
                <FloatingMenu
                    label="Agreement actions"
                    className="btn btn-sm btn-neutral rounded-2 d-flex align-items-center justify-content-center"
                    style={{ width: 28, height: 28, padding: 0, flexShrink: 0, alignSelf: 'flex-start' }}
                    renderLabel={() => <Icon name="ellipsis-v" />}
                >
                    <MenuItem
                        label="Edit"
                        icon={{ name: 'edit', className: 'me-2' }}
                        onClick={() => onEdit(agreement)}
                    />
                    <MenuItem
                        label="Preview"
                        icon={{ name: 'eye', className: 'me-2' }}
                        onClick={() => onPreview(agreement)}
                    />
                    <MenuItem
                        label="Version History"
                        icon={{ name: 'clock-rotate-left', className: 'me-2' }}
                        onClick={() => onVersionHistory(agreement)}
                    />
                    {canDelete && (
                        <MenuItem
                            label="Delete"
                            icon={{ name: 'trash', className: 'me-2', color: 'danger-text-emphasis' }}
                            onClick={() => onDelete(agreement)}
                            className="text-danger-emphasis"
                        />
                    )}
                </FloatingMenu>
            </Row>
            <Row valign="center" align="between">
                <Badge
                    color={agreement.enabled ? 'success' : 'secondary'}
                    style={{ fontSize: '0.75rem' }}
                >
                    {agreement.enabled ? 'Active' : 'Disabled'}
                </Badge>
                {updatedAt && (
                    <Text color="muted" style={{ fontSize: '0.8rem' }}>
                        {updatedAt}
                    </Text>
                )}
            </Row>
        </div>
    )
})

AgreementCard.displayName = 'AgreementCard'

const AgreementCardPlaceholder: React.FC = () => (
    <div className="border rounded p-3 mb-3">
        <Row valign="center" align="between" className="mb-2">
            <ContentPlaceholder width="25%" height={16} />
            <ContentPlaceholder width={28} height={28} />
        </Row>
        <Row valign="center" align="between">
            <ContentPlaceholder width={60} height={22} />
            <ContentPlaceholder width={80} height={14} />
        </Row>
    </div>
)

export { AgreementCard, AgreementCardPlaceholder }
